// TechnologyConsulting.js

import React, { useEffect } from "react";
import { Container, Box, Typography, Grid, Button, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import SecurityIcon from "@mui/icons-material/Security";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AssessmentIcon from "@mui/icons-material/Assessment";

export default function TechnologyConsulting() {
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const DARK_NAVY = "#0A192F";
  const LIGHT_GRAY = "#B0BEC5";
  const PRIMARY_GREEN = theme.palette.success.main;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const focusAreas = [
    {
      icon: <ArchitectureIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Architecture & Systems Design",
      description:
        "Design scalable, resilient, and maintainable systems that support your product roadmap and long‑term vision.",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Security & Compliance",
      description:
        "Identify risks early and align your stack with security best practices, regulatory needs, and data protection standards.",
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Cost Optimization",
      description:
        "Reduce cloud, infra, and tooling spend through smarter architecture, right‑sizing, and automation strategies.",
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Product Strategy & Roadmap",
      description:
        "Translate business vision into pragmatic product roadmaps with clear priorities, milestones, and technical feasibility.",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 8, md: 12 },
        backgroundColor: DARK_NAVY,
        color: "white",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Grid container spacing={6} alignItems="center">
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: "2.3rem", sm: "3rem", md: "3.3rem" },
                    mb: 2,
                    lineHeight: 1.2,
                  }}
                >
                  Strategic <Box component="span" sx={{ color: PRIMARY_GREEN }}>Technology Consulting</Box>
                  for founders and teams.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h6" sx={{ color: LIGHT_GRAY, fontWeight: 300, mb: 3 }}>
                  We combine 11+ years of hands‑on IT delivery with 4 years of US enterprise
                  experience to help you make high‑stakes technology decisions with clarity.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="body1" sx={{ color: LIGHT_GRAY, mb: 4, fontWeight: 300 }}>
                  Whether you are validating an MVP, scaling a fast‑growing product, or stabilizing a
                  complex system, we work with your leadership, product, and engineering teams to align
                  technology choices with business outcomes.
                </Typography>
              </motion.div>

              <motion.div
                variants={itemVariants}
                style={{ display: "flex", gap: theme.spacing(2), flexWrap: "wrap" }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/contact")}
                >
                  Book a Consultation
                </Button>

                <Button
                  variant="outlined"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/services")}
                >
                  View All Services
                </Button>
              </motion.div>
            </Grid>

            {/* RIGHT CONTENT: FOCUS AREAS */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {focusAreas.map((area) => (
                  <Grid item xs={12} sm={6} key={area.title}>
                    <motion.div
                      variants={itemVariants}
                      style={{
                        background: "rgba(15, 23, 42, 0.9)",
                        borderRadius: 16,
                        padding: "18px 16px",
                        border: "1px solid rgba(148, 163, 184, 0.25)",
                        height: "100%",
                      }}
                    >
                      {area.icon}
                      <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
                        {area.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: LIGHT_GRAY, fontSize: 13 }}>
                        {area.description}
                      </Typography>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
}